import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { Link, useLocation } from "react-router-dom";
import _ from "lodash";
import moment from "moment";
import { toast } from "react-toastify";
import Pagination from "./common/Pagination";
import { paginate, getFieldValue } from "../scripts/helperScripts";
import Input from "./common/Input";
import Select from "./common/Select";
import Table from "./common/Table";
import { getFormsySubmissionsByFormId } from "./services/soar";
import ToggleSwitch from "./common/ToggleSwitch";
import bintiOptions from "../options/binti.json";

function BulkCaseNotes() {
  const [caseNotes, setCaseNotes] = useState([]);
  const [loadStatus, setLoadStatus] = useState({
    loading: false,
    loaded: false,
  });
  const [data, setData] = useState({
    BeginDate: moment().subtract(1, "months").format(moment.HTML5_FMT.DATE),
    EndDate: moment().format(moment.HTML5_FMT.DATE),
    SortType: "Open",
  });

  const [totalCount, setTotalCount] = useState();
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState({
    path: "CreatedOn",
    order: "desc",
  });

  const location = useLocation();
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  const isMountedRef = useRef(true);
  const pageSize = 10;

  const userBusinessUnit = useMemo(() => {
    if (!safyInfo?.user?.safyInfo?.BusinessUnitIdName) {
      return;
    }

    return safyInfo.user.safyInfo.BusinessUnitIdName;
  }, [safyInfo]);

  const userIsAdmin = useMemo(() => {
    if (!safyInfo?.user?.modules) {
      return false;
    }

    return safyInfo.user.modules.includes("Admin");
  }, [safyInfo]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      !userBusinessUnit ||
      userBusinessUnit === "ccplive" ||
      !data.SelectedState ||
      data.SelectedState["@value"]
    ) {
      return;
    }

    const obj = { ...data };
    obj.SelectedState["@value"] = userBusinessUnit
      .replace("SAFY of ", "")
      .replace(" ", "")
      .toLowerCase();
    setData(obj);
  }, [userBusinessUnit, data]);

  useEffect(() => {
    if (loadStatus.loaded || loadStatus.loading || !userBusinessUnit) {
      return;
    }

    const loadBulkCaseNotes = async () => {
      setLoadStatus({ loading: true, loaded: false });
      const toastId = toast.loading("Loading Bulk Case Notes...", {
        type: toast.TYPE.INFO,
      });
      const response = await getFormsySubmissionsByFormId(
        "B98CE6B8-5C22-EE11-87DD-0050F21C653C"
      );

      if (!isMountedRef.current) {
        return;
      }

      if (!response) {
        toast.update(toastId, {
          render: "No Bulk Case Notes available.",
          isLoading: false,
          type: toast.TYPE.WARNING,
          autoClose: 5000,
          closeOnClick: true,
        });
        setCaseNotes([]);
        return;
      }

      const caseNotesResult = response.filter((caseNote) => {
        if (caseNote.Active !== "1") {
          return false;
        }

        if (userBusinessUnit === "ccplive") {
          return true;
        } else {
          const userState = userBusinessUnit
            .replace("SAFY of ", "")
            .replace(" ", "")
            .toLowerCase();

          return (
            caseNote.FormResults.results.SelectedState["@value"] === userState
          );
        }
      });

      setCaseNotes(caseNotesResult);
      setLoadStatus({ loaded: true, loading: false });

      if (caseNotesResult.length > 0) {
        toast.update(toastId, {
          render: "Bulk Case Notes loaded.",
          isLoading: false,
          type: toast.TYPE.SUCCESS,
          autoClose: 5000,
          closeOnClick: true,
        });
      } else {
        toast.update(toastId, {
          render: "No Bulk Case Notes available for selected date range.",
          isLoading: false,
          type: toast.TYPE.WARNING,
          autoClose: 5000,
          closeOnClick: true,
        });
      }
    };

    loadBulkCaseNotes();
  }, [loadStatus, userBusinessUnit]);

  const handleChange = async ({ currentTarget: input }) => {
    const obj = { ...data };
    obj[input.name] = input.value;
    setData(obj);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSort = (sortColumn) => {
    setSortColumn(sortColumn);
  };

  const getPagedData = useCallback(() => {
    if (!loadStatus.loaded || !caseNotes?.filter) {
      return [];
    }

    const filtered = caseNotes.filter((bulkCaseNote) => {
      const finalSigned = bulkCaseNote.FinalSigned === "1";

      if (data.SortType === "Open" && finalSigned) {
        return false;
      }

      if (data.SortType === "Submitted" && !finalSigned) {
        return false;
      }

      if (userBusinessUnit === "ccplive") {
        if (
          bulkCaseNote.FormResults.results.SelectedState["@value"] !==
          data.SelectedState
        ) {
          return false;
        }

        const createdOn = moment(bulkCaseNote.CreatedOn).startOf("d");
        const begin = data.BeginDate ? moment(data.BeginDate) : undefined;
        const end = data.EndDate ? moment(data.EndDate) : undefined;

        if (begin && !begin.isSameOrBefore(createdOn)) {
          return false;
        }

        if (end && !end.isSameOrAfter(createdOn)) {
          return false;
        }
      } else {
        const userState = userBusinessUnit
          .replace("SAFY of ", "")
          .replace(" ", "")
          .toLowerCase();

        if (
          bulkCaseNote.FormResults.results.SelectedState["@value"] !== userState
        ) {
          return false;
        }

        const createdOn = moment(bulkCaseNote.CreatedOn).startOf("d");
        const begin = data.BeginDate ? moment(data.BeginDate) : undefined;
        const end = data.EndDate ? moment(data.EndDate) : undefined;

        if (begin && !begin.isSameOrBefore(createdOn)) {
          return false;
        }

        if (end && !end.isSameOrAfter(createdOn)) {
          return false;
        }
      }

      return true;
    });

    const tableData = filtered.map((bulkCaseNote) => {
      const typeValue = getFieldValue(bulkCaseNote.FormResults.results.Type);
      const typeName = bintiOptions.ContactTypes.filter(
        (type) => type._id === typeValue
      )[0]?.name;
      return {
        VersionIntId: bulkCaseNote.VersionIntId,
        SubmissionId: bulkCaseNote.SubmissionId,
        FormVersionId: bulkCaseNote.FormVersionId,
        ContactType: typeName,
        ContactDate: moment(
          getFieldValue(bulkCaseNote.FormResults.results.Date)
        ).format("L"),
        Subject: getFieldValue(bulkCaseNote.FormResults.results.Subject),
        CreatedBy: bulkCaseNote.CreatedByName,
        CreatedOn: moment(bulkCaseNote.CreatedOn).format("L"),
        ProcessErrors: getFieldValue(
          bulkCaseNote.FormResults.results.ProcessErrors
        ),
      };
    });

    const sorted = _.orderBy(tableData, [sortColumn.path], [sortColumn.order]);

    if (totalCount !== sorted.length) setTotalCount(sorted.length);

    return paginate(sorted, currentPage, pageSize);
  }, [
    caseNotes,
    sortColumn,
    currentPage,
    totalCount,
    data,
    loadStatus,
    userBusinessUnit,
  ]);

  const columns = [
    {
      key: "CreatedBy",
      path: "CreatedBy",
      label: "Created By",
    },
    {
      key: "CreatedOn",
      path: "CreatedOn",
      label: "Created On",
    },
    {
      key: "ContactType",
      path: "ContactType",
      label: "Contact Type",
    },
    {
      key: "ContactDate",
      path: "ContactDate",
      label: "Contact Date",
    },
    {
      key: "Subject",
      path: "Subject",
      label: "Subject",
    },
    {
      key: "Open",
      content: (note) => {
        return (
          <Link
            title="Open Bulk Case Note"
            to={`/${
              note.VersionIntId === "1"
                ? "BulkCaseNote"
                : `BulkCaseNotev${note.VersionIntId}`
            }/${note.FormVersionId}/${note.SubmissionId}`}
            className={`${
              note.ProcessErrors && JSON.parse(note.ProcessErrors).length > 0
                ? "text-danger"
                : "text-primary"
            }`}
          >
            <i className="fas fa-pen" />
          </Link>
        );
      },
    },
  ];

  return (
    <>
      <div className="h5">Bulk Case Note</div>
      {userBusinessUnit === "ccplive" || userIsAdmin ? (
        <Select
          name="SelectedState"
          label="Select State"
          value={data.SelectedState}
          options={bintiOptions.States}
          onChange={handleChange}
        ></Select>
      ) : null}
      {!(userBusinessUnit === "ccplive" || userIsAdmin) ||
      data.SelectedState ? (
        <>
          <div className="container">
            <div className="row">
              <div className="col">
                <Input
                  name="BeginDate"
                  label="Begin Date"
                  type="date"
                  value={data.BeginDate}
                  onChange={handleChange}
                />
              </div>
              <div className="col">
                <Input
                  name="EndDate"
                  label="End Date"
                  type="date"
                  value={data.EndDate}
                  onChange={handleChange}
                />
              </div>
              <div className="col d-flex pt-3 mt-3">
                <ToggleSwitch
                  values={["Open", "Submitted"]}
                  selected={data.SortType}
                  onChange={(value) => {
                    setData({ ...data, SortType: value });
                  }}
                />
              </div>
              <div className="col pt-3 mt-2">
                <Link
                  className="btn btn-primary"
                  to={
                    process.env.REACT_APP_ENVIRONMENT === "Production"
                      ? "/BulkCaseNote/907FC923-6022-EE11-87DD-0050F21C653C"
                      : "/BulkCaseNotev2/1F160FF9-F679-EF11-991A-C66FF475DD83"
                  }
                  state={{
                    previousPath: location.pathname,
                    selectedState: data.SelectedState,
                  }}
                >
                  Add Bulk Case Note
                </Link>
              </div>
            </div>
          </div>
          <Table
            columns={columns}
            data={getPagedData()}
            sortColumn={sortColumn}
            onSort={handleSort}
            idColumn="SubmissionId"
          />
          {loadStatus.loaded ? (
            caseNotes.length === 0 ? (
              "No Bulk Case Notes for the given date range..."
            ) : (
              <div className="row justify-content-center">
                <Pagination
                  itemsCount={totalCount}
                  pageSize={pageSize}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                />
              </div>
            )
          ) : null}
        </>
      ) : null}
    </>
  );
}

export default BulkCaseNotes;
